import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { authAPI, setAuthToken } from '../services/api';

const AuthContext = createContext();

const getFrontendBaseUrl = () => process.env.REACT_APP_FRONTEND_URL || 'https://3pl.datavisual.ca';

// Check if token is close to expiration (within 5 minutes)
const isTokenNearExpiry = (token) => {
  if (!token) return false;

  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;

    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    const currentTime = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = payload.exp - currentTime;

    return timeUntilExpiry < 300;
  } catch (error) {
    return false;
  }
};

// Refresh token proactively
const refreshTokenProactively = async () => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/auth/refresh-token`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      const data = await response.json();
      if (data.token) {
        localStorage.setItem('token', data.token);
        if (data.user) {
          localStorage.setItem('user', JSON.stringify(data.user));
        }
        return true;
      }
    }

    return false;
  } catch (error) {
    console.error('Token refresh error:', error);
    return false;
  }
};

// Provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const loadingRef = useRef(false);

  // Load user from local storage on initial render
  useEffect(() => {
    const loadUser = () => {
      if (loadingRef.current) return;
      loadingRef.current = true;

      try {
        const storedUser = localStorage.getItem('user');
        const token = localStorage.getItem('token');

        if (storedUser && token) {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
        } else {
          setUser(null);
        }
      } catch (err) {
        console.error('Failed to load user from storage:', err);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        setUser(null);
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    };

    loadUser();
  }, []);

  // Monitor token expiration and refresh proactively
  useEffect(() => {
    let intervalId;

    const checkAndRefreshToken = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (token && storedUser && isTokenNearExpiry(token)) {
        const refreshSuccess = await refreshTokenProactively();

        if (refreshSuccess) {
          const updatedUser = localStorage.getItem('user');
          if (updatedUser) {
            try {
              setUser(JSON.parse(updatedUser));
            } catch (error) {
              // Corrupted data will be caught on next auth check
            }
          }
        }
      }
    };

    if (user) {
      intervalId = setInterval(checkAndRefreshToken, 2 * 60 * 1000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [user]);

  // Login
  const login = async (credentials) => {
    try {
      setError(null);
      const response = await authAPI.login(credentials);
      const { user: userData, token } = response.data;

      // Store token first, then user data
      if (token) {
        localStorage.setItem('token', token);
      }
      localStorage.setItem('user', JSON.stringify(userData));

      setUser(userData);
      return userData;
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
      throw err;
    }
  };

  // Logout
  const logout = () => {
    setAuthToken(null);

    localStorage.removeItem('token');
    localStorage.removeItem('user');

    setUser(null);

    window.dispatchEvent(new CustomEvent('authChange', {
      detail: { type: 'logout' }
    }));

    window.location.href = getFrontendBaseUrl();
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    const token = localStorage.getItem('token');
    return !!token && !!user;
  };

  // Update user profile in context after changes
  const updateUserProfile = (updatedData) => {
    const updated = { ...user, ...updatedData };
    setUser(updated);
    localStorage.setItem('user', JSON.stringify(updated));
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    isAuthenticated,
    updateUserProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
